import React from 'react';
import BundleTiers from './BundleTiers';
import SelectedBundleDisplay from './SelectedBundleDisplay';
import OrderItem from './OrderItem';
import {
    shouldShowIndividualItems,
    calculateHasExtrasOutsideBundle,
    getIndividualItemsHeader,
    getIndividualItemsDescription
} from '../utils/purchaseLogic';
import { useProductSelectionState } from '../hooks/useProductSelectionState';

/**
 * ProductSelection Component
 * Handles all product selection logic including bundle tiers, custom purchase, and individual items
 * Extracted from Order.js for better maintainability and reusability
 */
const ProductSelection = ({
    // Bundle state
    bundleOptions,
    selectedBundle,
    bundleItems,
    bundleStatus,
    isCustomPurchase,
    
    // Additional items state
    additionalQuantities,
    
    // Order data
    order,
    teamPrimaryColor,
    
    // Event handlers
    onSelectBundle,
    onPreviewBundle,
    onRemoveBundle,
    onOptionChange,
    onIncCount,
    onDecCount,
    getBundleQuantity
}) => {
    // Determine if bundles are available
    const hasBundles = bundleOptions && bundleOptions.length > 0;
    
    // Check if user has extras outside of bundle
    const hasExtrasOutsideBundle = React.useMemo(() => {
        return calculateHasExtrasOutsideBundle(selectedBundle, order, getBundleQuantity);
    }, [selectedBundle, order, getBundleQuantity]);
    
    // Use custom hook for ProductSelection state management
    const {
        showIndividualItems,
        userHasToggled,
        toggleIndividualItems,
        resetForMode
    } = useProductSelectionState(hasExtrasOutsideBundle);
    
    // Reset states when switching between modes (but NOT when hasExtrasOutsideBundle changes)
    React.useEffect(() => {
        resetForMode(isCustomPurchase, selectedBundle, hasExtrasOutsideBundle);
    }, [isCustomPurchase, selectedBundle, resetForMode]); // Removed hasExtrasOutsideBundle from dependencies
    

    
    return (
        <>
            {/* Bundle Tiers Selection - Only show if bundles are available */}
            {hasBundles && (
                <div style={{
                    backgroundColor: '#fff',
                    padding: '2rem',
                    borderRadius: '10px',
                    marginBottom: '2rem',
                    border: '1px solid #dee2e6'
                }}>
                    <BundleTiers
                        bundles={bundleOptions}
                        selectedBundle={selectedBundle}
                        onSelectBundle={onSelectBundle}
                        onPreviewBundle={onPreviewBundle}
                        teamPrimaryColor={teamPrimaryColor}
                    />
                    
                    {/* Custom Purchase Button - Like custom donation amount */}
                    <div className="text-center mt-4">
                        <button 
                            type="button"
                            className="btn btn-link"
                            onClick={() => onSelectBundle(null)}
                            style={{ 
                                color: teamPrimaryColor || '#0e456c',
                                textDecoration: isCustomPurchase ? 'underline' : 'none'
                            }}
                        >
                            Or choose individual items (Custom Purchase)
                        </button>
                    </div>
                </div>
            )}

            {/* Selected Bundle Display */}
            {selectedBundle && (
                <SelectedBundleDisplay
                    selectedBundle={selectedBundle}
                    bundleItems={bundleItems}
                    teamPrimaryColor={teamPrimaryColor}
                    bundleStatus={bundleStatus}
                    onRemoveBundle={onRemoveBundle}
                    onOptionChange={onOptionChange}
                    order={order}
                    hasExtrasOutsideBundle={hasExtrasOutsideBundle}
                    showIndividualItems={showIndividualItems}
                    onToggleIndividualItems={toggleIndividualItems}
                />
            )}



            {/* Individual Items Section - Show if Custom Purchase is selected OR no bundles available OR individual items expanded */}
            {shouldShowIndividualItems(isCustomPurchase, hasBundles, selectedBundle, showIndividualItems, order) && (
                <div style={{
                    backgroundColor: '#fff',
                    padding: '2rem',
                    borderRadius: '10px',
                    marginBottom: '2rem',
                    border: '1px solid #dee2e6'
                }}>
                    {/* Dynamic header based on context */}
                    {(() => {
                        const header = getIndividualItemsHeader(hasBundles, isCustomPurchase, selectedBundle, showIndividualItems);
                        const description = getIndividualItemsDescription(hasExtrasOutsideBundle, selectedBundle, showIndividualItems, hasBundles, isCustomPurchase);
                        
                        return (
                            <>
                                <h3 style={{ color: teamPrimaryColor || '#0e456c', marginBottom: '1.5rem' }}>
                                    {header.emoji && `${header.emoji} `}{header.title}
                                </h3>
                                <p className="text-muted mb-3">
                                    {description}
                                </p>
                            </>
                        );
                    })()}
                    
                    {/* Show loading message if no items loaded yet */}
                    {(!order.fundraiserOrderItems || order.fundraiserOrderItems.length === 0) && (
                        <div className="text-center text-muted py-4">
                            <p>Loading items...</p>
                        </div>
                    )}
                    
                    {/* Render items if they exist */}
                    {order.fundraiserOrderItems && order.fundraiserOrderItems.map((item, index) => {
                        return (
                            <div key={item.itemId}>
                                <OrderItem 
                                    item={item} 
                                    fundraiserId={order.fundraiserId} 
                                    itemId={item.itemId} 
                                    incCount={onIncCount} 
                                    decCount={onDecCount}
                                    // Pass bundle context for enhanced display
                                    bundleSelected={!!selectedBundle && !isCustomPurchase}
                                    getBundleQuantity={getBundleQuantity}
                                    showBundleInfo={selectedBundle && showIndividualItems}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

export default ProductSelection;
